import React from 'react'
import { makeObservable, observable, computed, action } from 'mobx'
import _, { get, set, isArray } from 'lodash'
import { DO_LOAD, getGMTOffset, objectHasMissingValues } from '../../config'
import { convertEmptyArraysToEmptyObject } from '../../config/utils'
import { translate } from '../../cls/TranslationContext'
import dayjs from 'dayjs'

/**
 * Class representing a User.
 * Contains user information and methods to manipulate and update user data.
 *
 * @class User
 */
export class User {
	_class = 'User'
	loaded = -1 // -1 means not loaded, -2 means new flag, 1 means loaded compact, timestamp means loaded full

	user_id = ''
	email = ''

	// When full loaded
	allowed = {} // Contains user permissions
	full = { name: '' } // Contains other important user information
	compact = { name: '' }
	userIds = [] // Contains list of all user IDs (assigned to admin only)
	modified = ''
	track_modified = ''
	modified_related = ''
	track_modified_related = ''
	to_delete = false

	temporarySubscriptions = [] // Temporary subscriptions for the user
	temporaryPurchases = [] // Temporary purchases for the user

	/**
	 * constructor
	 * @description Initializes a new User instance with observables and computed properties.
	 * @param {string} userId - The unique identifier of the user.
	 */
	constructor(userId) {
		this.user_id = userId

		makeObservable(this, {
			loaded: observable,
			requiredActions: computed,

			// ### Compact ###
			user_id: observable,
			email: observable,
			setEmail: action.bound,
			clientsNr: computed,
			teamNr: computed,

			contractMonthlyHours: computed,
			contractNumber: computed,
			contractDate: computed,
			setContractMonthlyHours: action.bound,
			setContractNumber: action.bound,
			setContractDate: action.bound,

			// Mark as to delete or undo delete
			toDeleteTs: computed,
			setToDelete: action.bound,

			modifiedTs: computed,
			track_modified: observable,
			trackModifiedTs: computed,
			setTrackModified: action.bound,
			track_modified_related: observable,
			modifiedRelatedTs: computed,
			trackModifiedRelatedTs: computed,
			setTrackModifiedRelated: action.bound,

			// ### Full ###
			allowed: observable,
			full: observable,
			compact: observable,

			// ### Full helpers ###
			country: computed,
			businessDetails: computed,
			setBusinessDetails: action.bound,

			businessName: computed,
			businessId: computed,

			// Subscriptions
			subscriptions: computed,
			setSubscriptions: action.bound,
			temporarySubscriptions: observable,
			setTemporarySubscriptions: action.bound,

			syncSubscriptions: action.bound,

			// Purchases
			purchases: computed,
			setPurchaseStatus: action.bound,

			temporaryPurchases: observable,
			setTemporaryPurchases: action.bound,

			// Grouping platforms
			platforms: computed,
			platformsIds: computed,
			setPlatforms: action.bound,

			aiWpChat: computed,
			updateAiWpChatToken: action.bound,

			// Grouping generals
			name: computed,
			setName: action.bound,

			clients: computed,
			setClients: action.bound,

			team: computed,
			setTeam: action.bound,

			clientAndTeamIds: computed,

			preferences: computed,
			setPreferences: action.bound,

			cookieConsent: computed,
			setCookieConsent: action.bound,

			// Server Actions
			setNewFlag: action,
			resetLastFullLoad: action,
			updateFromServer: action,

			// Helper methods
			singleUserId: computed,

			// seller promo codes
			promoCodes: computed,
			setPromoCodes: action.bound,
			getActivePromoCodes: computed,
			getActivePromoCodesString: computed
		})
	}

	/**
	 * Retrieves the user's required actions.
	 * Meaning the actions that the user must take to complete their profile.
	 * Returns an array like: [{id:'name', label: 'Add your name', status: false/true, link: '/profile'}] were each action
	 * is an object with an id, label, status and link the user can click to go to the action.
	 * @returns {Array} The user's required actions.
	 */
	get requiredActions() {
		return [
			{
				id: 'name',
				label: translate('__add-your%single.name'),
				status: !!get(this.full, ['name'], false),
				link: '/users/profile/edit?tab=details&section=section-general'
			},
			{
				id: 'businessName',
				label: translate('single.business-name'),
				status: !!get(
					this.full,
					['businessDetails', 'business_name'],
					false
				),
				link: '/users/profile/edit?tab=details&section=section-business'
			},
			{
				id: 'businessId',
				label: translate('single.business-id'),
				status: !!get(
					this.full,
					['businessDetails', 'business_id'],
					false
				),
				link: '/users/profile/edit?tab=details&section=section-business'
			},
			{
				id: 'businessDetails',
				label: translate('single.business-details.label'),
				status: !objectHasMissingValues(
					get(this.full, ['businessDetails'], {}),
					['address_line_2', 'state']
				),
				link: '/users/profile/edit?tab=details&section=section-business'
			},
			{
				id: 'contract',
				label: translate('single.contract-hours'),
				status: this.isTeam
					? !!get(this.full, ['contract', 'monthly_hours'], false)
					: true,
				link: '/users/profile/edit?tab=details&section=section-contact'
			},
			{
				id: 'platforms',
				label: translate('single.missing-platforms'),
				status: this.isClient ? this.platformsIds.length !== 0 : true,
				link: '/users/profile/edit?tab=platforms&section=section-platforms'
			}
		].filter((action) => action.status === false)
	}

	get aiWpChat() {
		return get(this.full, ['ai_wp_chat'], {
			site_url: '',
			openai_api_key: '',
			subscription_id: '',
			token: '',
			openai_validated_at: null,
			openai_validated_for: ''
		})
	}

	get aiWpChatToken() {
		return this.aiWpChat?.token || ''
	}

	get aiWpChatSubscriptionId() {
		return this.aiWpChat?.subscription_id || ''
	}

	updateAiWpChatToken(updatedTokenValues) {
		// we need to update only the token values, not overwrite the whole object
		const currentAiWpChat = this.aiWpChat || {}
		const newAiWpChat = { ...currentAiWpChat, ...updatedTokenValues }
		this.chgInFull(['ai_wp_chat'], newAiWpChat)
	}

	get discount() {
		return get(this.full, ['discount'], '')
	}
	setDiscount(value) {
		this.chgInFull(['discount'], value)
	}

	get promoCodes() {
		return get(this.full, ['promoCodes'], {})
	}

	setPromoCodes(codes) {
		this.chgInFull(['promoCodes'], codes)
	}

	/**
	 * Return promo-codes that are `true` in **every** region.
	 * @param {Object} obj  – keyed by region (EU, US, …)
	 * @returns {string[]}  – codes active in all regions
	 */
	get getActivePromoCodes() {
		const promoCodes = this.promoCodes || {}
		if (Object.keys(promoCodes).length === 0) {
			return []
		}
		return Object.values(promoCodes)
			.map(({ codes }) =>
				Object.entries(codes)
					.filter(([, isActive]) => isActive)
					.map(([code]) => code)
			)
			.reduce((a, b) => a.filter((code) => b.includes(code)))
	}

	get getActivePromoCodesString() {
		return this.getActivePromoCodes.map((code, index) => (
			<span key={index}>
				{code}
				{index < this.getActivePromoCodes.length - 1 ? ', ' : ''}
			</span>
		))
	}

	/**
	 * Retrieves the user's cookieConsent.
	 *
	 * @returns {Object} The user's cookieConsent.
	 */
	get cookieConsent() {
		return get(this.full, ['cookieConsent'], undefined)
	}

	/**
	 * Sets the user's cookieConsent.
	 *
	 * @param {Object} value - The cookieConsent to set.
	 */
	setCookieConsent(value) {
		this.chgInFull(['cookieConsent'], value)
	}

	/**
	 * Retrieves the user's preferences.
	 *
	 * @returns {Object} The user's preferences, or an empty object if none exist.
	 */
	get preferences() {
		return get(this.full, ['preferences'], {})
	}

	/**
	 * Sets the user's preferences.
	 *
	 * @param {Object} value - The preferences to set.
	 */
	setPreferences(value) {
		this.chgInFull(['preferences'], value || {})
	}

	// ### COMPACT ###
	/**
	 * Sets the email of the User.
	 *
	 * @param {string} email - The email to set.
	 */
	setEmail(email) {
		this.email = email
	}

	/**
	 * Gets the number of associated clients from the compact data.
	 */
	get clientsNr() {
		return get(this.compact, ['count_clients'], 0)
	}

	/**
	 * Gets the number of associated team members from the compact data.
	 */
	get teamNr() {
		return get(this.compact, ['count_team'], 0)
	}

	/**
	 * Gets the Unix timestamp of when the user was last modified.
	 */
	get modifiedTs() {
		return this.modified && dayjs(this.modified).unix()
	}

	/**
	 * Gets the Unix timestamp for the tracked modification field.
	 */
	get trackModifiedTs() {
		return this.track_modified && dayjs(this.track_modified).unix()
	}

	/**
	 * Updates the internal field tracking when the user was last modified.
	 */
	setTrackModified(value) {
		this.track_modified = value
	}

	/**
	 * Gets the Unix timestamp of when related user data was last modified.
	 */
	get modifiedRelatedTs() {
		return this.modified_related && dayjs(this.modified_related).unix()
	}

	/**
	 * Sets the tracked related modification field to the provided value.
	 */
	setTrackModifiedRelated(value) {
		this.track_modified_related = value
	}

	/**
	 * Gets the Unix timestamp from the tracked related modification field.
	 */
	get trackModifiedRelatedTs() {
		return (
			this.track_modified_related &&
			dayjs(this.track_modified_related).unix()
		)
	}

	/**
	 * Gets the Unix timestamp representing when the user is scheduled for deletion, or null if not set.
	 */
	get toDeleteTs() {
		return this.to_delete ? dayjs(this.to_delete).unix() : null
	}

	/**
	 * Retrieves the monthly contract hours from the full object.
	 */
	get contractMonthlyHours() {
		return get(this.full, ['contract', 'monthly_hours'], '')
	}

	/**
	 * Retrieves the user's contract number from the full object.
	 */
	get contractNumber() {
		return get(this.full, ['contract', 'number'], '')
	}

	/**
	 * Retrieves the contract date range as an array of dayjs objects.
	 */
	get contractDate() {
		const data = get(this.full, ['contract', 'date'], [])
		return data.map((val) => dayjs(val))
	}

	/**
	 * Sets the monthly contract hours value in the full object.
	 */
	setContractMonthlyHours(value) {
		this.chgInFull(['contract', 'monthly_hours'], value)
	}

	/**
	 * Sets the contract number value in the full object.
	 */
	setContractNumber(value) {
		this.chgInFull(['contract', 'number'], value)
	}

	/**
	 * Sets the contract date as an array in the full object.
	 */
	setContractDate(dates) {
		this.chgInFull(['contract', 'date'], dates || [])
	}

	/**
	 * Sets the user deletion timestamp (or false to undo deletion).
	 */
	setToDelete(val) {
		this.to_delete = val
	}

	// ### Full helpers ###
	/**
	 * Gets the name of the User.
	 * If the name is not set in the full object, returns the email.
	 *
	 * @returns {string} The name of the User.
	 */
	get name() {
		// fallbacks in order: full, compact, email
		// return get(this.full, ['name'], get(this.compact, ['name'], this.email))
		const fullName = get(this.full, ['name'], '').trim()
		const compactName = get(this.compact, ['name'], '').trim()
		return fullName || compactName || this.email
	}
	/**
	 * Sets the name of the User.
	 * Updates the full object with the new name.
	 *
	 * @param {string} value - The name to set.
	 */
	setName(value) {
		this.chgInFull(['name'], value)
	}

	/**
	 * Gets the clients associated with the User.
	 * Returns an empty array if no clients are set.
	 *
	 * @returns {Array<string>} The list of client IDs.
	 */
	get clients() {
		return get(this.full, ['clients'], [])
	}

	/**
	 * Sets the clients associated with the User.
	 * Ensures the value is an array of string IDs before updating the full object.
	 *
	 * @param {Array<string>} value - The list of client IDs to set.
	 */
	setClients(value) {
		if (isArray(value) && value.every((id) => typeof id === 'string')) {
			this.full.clients = value
		} else if (value) {
			console.error('Clients must be an array of string IDs')
		}
	}

	/**
	 * Get the team members associated with the User.
	 * Returns an empty array if no team members are set.
	 *
	 * @returns {Array<string>} The list of team member IDs.
	 */
	get team() {
		return get(this.full, ['team'], [])
	}

	/**
	 * Sets the team members associated with the User.
	 * Ensures the value is an array of string IDs before updating the full object.
	 *
	 * @param {Array<string>} value - The list of team member IDs to set.
	 * @returns {void}
	 */
	setTeam(value) {
		if (isArray(value) && value.every((id) => typeof id === 'string')) {
			this.full.team = value
		}
	}

	get clientAndTeamIds() {
		// Returns a unique list of client and team IDs
		const ids = [...this.clients, ...this.team]
		return [...new Set(ids)]
	}

	/**
	 * This method, checks if user has anybody in team or clients, if only one of them, then it returns its id, else returns self id
	 * Used as quick method of selecting a user for communication or actions.
	 */
	get singleUserId() {
		if (this.clients.length === 1) {
			return this.clients[0]
		} else if (this.team.length === 1) {
			return this.team[0]
		}
		return this.user_id
	}

	// ### Grouping platforms ###
	get platforms() {
		return get(this.full, ['platforms'], [])
	}

	/**
	 * Sets the user's platforms and encrypts any plaintext passwords found in the platform inputs.
	 * For each platform object in the array, if an input object has type 'password' and is not encrypted
	 * (i.e., input.encrypted is false or missing), the password is encrypted using this.user_id as part of the key.
	 * The encrypted password can be decrypted on the backend using the user_id for security.
	 *
	 * @param {Array<Object>} platforms - Array of platform objects. Each platform contains an 'inputs' array.
	 * @param {Array<Object>} platforms[].inputs - Array of input objects for the platform.
	 * @param {string} platforms[].inputs[].type - The type of the input (e.g., 'password').
	 * @param {string} platforms[].inputs[].value - The value of the input (e.g., the password).
	 * @param {boolean} [platforms[].inputs[].encrypted] - Whether the input value is already encrypted.
	 *
	 * @returns {void}
	 */
	setPlatforms = (platforms) => {
		const encryptedPlatforms = (platforms || []).map((platform) => ({
			...platform,
			inputs: (platform.inputs || []).map((input) => {
				if (input.type === 'password' && !input?.encrypted) {
					// simple Base64 “encryption” with user_id prefix + timestamp
					const token = btoa(`${this.user_id}:${input.value}`)

					return {
						...input,
						value: token,
						encrypted: true,
						timestamp: dayjs().unix()
					}
				}
				return input
			})
		}))
		// platforms is an array of platform objects, each object has a key named inputs that is an array of objects, if input.encrypted is false or missing on input.type === 'password', than we need to encrypt it using this.user_id and decrypt in php and download out of server for security
		this.chgInFull(['platforms'], encryptedPlatforms || [])
	}

	get platformsIds() {
		return this.platforms.map((platform) => platform.id)
	}

	setTemporarySubscriptions(value) {
		this.temporarySubscriptions = value || []
	}

	setTemporaryPurchases(value) {
		this.temporaryPurchases = value || []
	}

	get subscriptions() {
		return get(this.full, ['subscriptions'], {})
	}

	setSubscriptions(value) {
		this.chgInFull(['subscriptions'], value || {})
	}

	// ### Subscriptions helpers ###

	// If user has any active subscription with category "Backline" we will activate all categories. Return true if any active subscription is found.
	get hasActiveBacklineSubscription() {
		const subscriptions = this.subscriptions || {}
		return Object.values(subscriptions).some(
			(sub) => sub.status === 'active' && sub.category === 'Backline'
		)
	}

	get activeCategoriesBasedOnSubscriptions() {
		const subscriptions = this.subscriptions || {}
		const activeSubscriptions = Object.entries(subscriptions).reduce(
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			(acc, [id, sub]) => {
				if (sub.status === 'active' || sub.status === 'trialing') {
					const category = sub?.category
					if (!category) {
						return acc
					}
					acc[category] = acc[category] || { category, status: false }
					acc[category].status = true
				}
				return acc
			},
			{}
		)
		return Object.values(activeSubscriptions)
			.filter((sub) => sub.status === true)
			.map((sub) => sub.category)
	}

	get activeCategoriesBasedOnPurchases() {
		const purchases = this.purchases || {}
		const activePurchases = Object.entries(purchases).reduce(
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			(acc, [id, purchase]) => {
				const category = purchase?.category
				if (!category) {
					return acc
				}
				acc[category] = acc[category] || { category, status: false }
				acc[category].status = true
				return acc
			},
			{}
		)
		return Object.values(activePurchases)
			.filter((purchase) => purchase.status === true)
			.map((purchase) => purchase.category)
	}

	syncSubscriptions(subscriptions, getPlanById) {
		// Should preserve object of curentSubscriptions (limited object with: id: {category (plan.metadata.category), start_date, cancel_at, cancel_at_period_end, cancellation_details, days_until_due}) get this data from subscriptions
		const currentSubscriptions = get(this.full, ['subscriptions'], {})
		const newSubscriptions = {}
		subscriptions.forEach((sub) => {
			const {
				id,
				plan,
				start_date,
				canceled_at,
				cancel_at,
				cancel_at_period_end,
				cancellation_details,
				days_until_due,
				trial_start,
				trial_end,
				status
			} = sub
			const planData = getPlanById(plan.id)
			newSubscriptions[id] = {
				priceId: plan.id,
				category: planData?.product?.metadata?.category || 'Other', // Extract category from plan metadata
				start_date,
				canceled_at,
				cancel_at,
				cancel_at_period_end,
				cancellation_details,
				days_until_due,
				trial_start,
				trial_end,
				status
			}
		})

		// Let's verify if equal
		if (
			Object.keys(currentSubscriptions).length !==
				Object.keys(newSubscriptions).length ||
			!_.isEqual(currentSubscriptions, newSubscriptions)
		) {
			// If not equal, update the full object
			this.chgInFull(['subscriptions'], newSubscriptions)
			return true
		}

		return false
	}

	syncPurchases(purchases, getPlanById) {
		// Should preserve object of currentPurchases (limited object with: id: {category (plan.metadata.category), start_date, cancel_at, cancel_at_period_end, cancellation_details, days_until_due}) get this data from purchases
		const currentPurchases = get(this.full, ['purchases'], {})
		const newPurchases = {}
		purchases.forEach((purchase) => {
			const { id, metadata, status } = purchase
			const planData = getPlanById(metadata.price_id)
			newPurchases[id] = {
				priceId: metadata.price_id,
				category: planData?.product?.metadata?.category || 'Other', // Extract category from plan metadata
				status
			}
		})

		// Let's verify if equal
		if (
			Object.keys(currentPurchases).length !==
				Object.keys(newPurchases).length ||
			!_.isEqual(currentPurchases, newPurchases)
		) {
			// If not equal, update the full object
			this.chgInFull(['purchases'], newPurchases)
			return true
		}

		return false
	}

	get purchases() {
		return get(this.full, ['purchases'], {})
	}

	setPurchases(value) {
		this.chgInFull(['purchases'], value || {})
	}

	get purchasesHistory() {
		return get(this.full, ['purchases_history'], {})
	}
	// Structured as { [id]: { status: 'completed || pending', ... } }
	setPurchaseStatus(id, value) {
		const purchasesHistory = get(this.full, ['purchases_history'], {})
		const item = get(purchasesHistory, [id], {})
		purchasesHistory[id] = {
			...item,
			status: value,
			updated_at: dayjs().unix() // Update the timestamp when status changes
		}
		this.chgInFull(['purchases_history'], purchasesHistory)
	}

	get businessName() {
		return get(
			this.compact,
			['business_name'],
			get(this.businessDetails, ['business_name'], '')
		)
	}

	get businessId() {
		return get(
			this.compact,
			['business_id'],
			get(this.businessDetails, ['business_id'], '')
		)
	}

	// Get business details, object example: { business_name: '', address: { address_line_1: '', address_line_2: '', city: '', state: '', zip_code: '' }, mobile: { prefix: '', number: '', status: 'not-verified' } }
	get businessDetails() {
		return get(this.full, ['businessDetails'], {
			business_name: '',
			business_id: '',
			timezone: '',
			address: {
				address_line_1: '',
				address_line_2: '',
				city: '',
				state: '',
				zip_code: ''
			},
			mobile: { prefix: '', number: '', status: 'not-verified' }
		})
	}

	get country() {
		// get from compact.country or from full.businessDetails.address.country
		return get(
			this.compact,
			['country'],
			get(this.businessDetails, ['address', 'country'], '')
		)
	}

	// Set business details
	setBusinessDetails(value) {
		// value contains all form data, we need to extract only business details keys
		this.chgInFull(['businessDetails'], {
			business_name: value.business_name,
			business_id: value.business_id,
			timezone: value.timezone || getGMTOffset(),
			address: {
				country: value.country,
				state: value.state,
				city: value.city,

				address_line_1: value.address_line_1,
				address_line_2: value.address_line_2,

				zip_code: value.zip_code
			},
			mobile: {
				prefix: value.prefix,
				number: value.number,
				status: value.status ? 'verified' : 'not-verified'
			}
		})
	}

	setAllowed(value) {
		this.allowed = value
	}

	// ### User helpers ###
	// read allowed and check if the user is super admin set to true
	get isSuperAdmin() {
		const superAdmin = get(this.allowed, ['superAdmin'], false)
		return superAdmin === true || superAdmin === 'true'
	}

	/*
	 * isAdmin() - Check if the user is an admin.
	 * - This permission provides access to restricted areas of the application.
	 * - Use this permission to determine if the user is an admin or super admin.
	 */
	get isAdmin() {
		const admin = get(this.allowed, ['admin'], false)
		return admin === true || admin === 'true' || this.isSuperAdmin
	}

	/*
	 * Roles
	 */

	get isClientAdmin() {
		const clientAdmin = get(this.allowed, ['roles', 'clientAdmin'], false)
		return clientAdmin === true || clientAdmin === 'true'
	}

	get isClient() {
		const client = get(this.allowed, ['roles', 'client'], false)
		return client === true || client === 'true'
	}

	get isTeamAdmin() {
		const teamAdmin = get(this.allowed, ['roles', 'teamAdmin'], false)
		return teamAdmin === true || teamAdmin === 'true'
	}

	get isTeam() {
		const team = get(this.allowed, ['roles', 'team'], false)
		return team === true || team === 'true'
	}

	// ### Server Actions ###
	/**
	 * Sets the loaded flag to indicate the User is new.
	 */
	setNewFlag() {
		this.loaded = -2
	}

	/**
	 * Resets the loaded flag to the current timestamp.
	 */
	resetLastFullLoad() {
		this.loaded = Date.now()
	}

	/**
	 * Helper function to change a value in the full object.
	 * @param {Array} path - The path to the property in the full object.
	 * @param {any} val - The value to set.
	 */
	chgInFull = (path, val) => {
		if (get(this.full, path) !== val) {
			set(this.full, path, val)
		}
	}

	/**
	 * Converts the User instance to a JSON object.
	 * only values that are set are included in the JSON are saved to LocalStorage.
	 * @returns {object} The JSON representation of the User.
	 */
	toJSON() {
		return {
			userId: this.user_id,
			email: this.email,
			allowed: this.allowed,
			full: this.full,
			userIds: this.userIds,
			modified: this.modified,
			modified_related: this.modified_related
		}
	}

	/**
	 * Updates the User with data from the server.
	 * If the full data is a string, it attempts to parse it as JSON.
	 *
	 * @param {object} data - The data from the server.
	 */
	updateFromServer(data) {
		// console.log('updateFromServer', data)
		// deconstruct data, and manually treat allowed and full
		const { allowed, email, full, compact, userIds } = data
		// console.log('updateFromServer', { email, allowed, full, userIds })

		if (email) {
			this.email = email
		}

		if (compact) {
			if (typeof compact === 'string') {
				try {
					this.compact = JSON.parse(compact)
				} catch (error) {
					console.error('Failed to parse compact JSON:', error)
				}
			} else {
				this.compact = compact
			}

			this.email = this.compact?.email || this.email
		}

		// Fix JSON parsing
		if (allowed) {
			if (typeof allowed === 'string') {
				try {
					this.allowed = JSON.parse(allowed)
				} catch (error) {
					console.error('Failed to parse allowed JSON:', error)
				}
			} else {
				this.allowed = allowed
			}

			// Convert any empty arrays in 'this.allowed' to empty objects
			// to ensure consistent data structure
			convertEmptyArraysToEmptyObject(this.allowed)
		}

		if (full) {
			if (typeof full === 'string') {
				try {
					const parsed = JSON.parse(full)
					// parse each key in full
					this.full = {}
					for (const key in parsed) {
						const val = parsed[key]
						if (typeof val === 'string') {
							try {
								this.full[key] = JSON.parse(val)
							} catch (error) {
								this.full[key] = val
							}
						} else {
							this.full[key] = val
						}
					}
				} catch (error) {
					console.error('Failed to parse full JSON:', error)
				}
			} else {
				this.full = full
			}
		}

		if (userIds) {
			this.userIds = userIds
		}

		// Assign data to class instance
		// Object.assign(this, rest)

		// Parse class instance
		if (this.full && Object.keys(this.full).length > 0) {
			this.resetLastFullLoad()
		} else {
			this.loaded = DO_LOAD
		}

		this.to_delete = data.to_delete || false
		this.modified = data.modified || null
		this.modified_related = data.modified_related || null
	}
}

export default User
